import React, { useState } from 'react';
import styled from 'styled-components';
import { SchemaElement, TemplateCategory, TemplateItem } from '../../types/mapEditor';
import { TEMPLATE_ITEMS, CATEGORY_NAMES, getTemplateIcon } from './templateData';

const LibraryContainer = styled.div<{ $isCollapsed: boolean }>`
  width: ${props => props.$isCollapsed ? '48px' : '280px'};
  height: 100%;
  background: #1a1a2e;
  border-left: 1px solid #333;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  overflow: hidden;
`;

const LibraryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid #333;
  min-height: 48px;
`;

const HeaderTitle = styled.h3<{ $visible: boolean }>`
  color: #fff;
  font-size: 14px;
  margin: 0;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.2s;
  white-space: nowrap;
`;

const CollapseButton = styled.button`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #2a2a3e;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CategoryTabs = styled.div<{ $visible: boolean }>`
  display: ${props => props.$visible ? 'flex' : 'none'};
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px;
  border-bottom: 1px solid #333;
`;

const CategoryTab = styled.button<{ $active: boolean }>`
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  background: ${props => props.$active ? '#4CAF50' : '#2a2a3e'};
  border: 1px solid ${props => props.$active ? '#4CAF50' : '#444'};
  border-radius: 4px;
  color: #FFFFFF;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$active ? '#45a049' : '#3a3a4e'};
  }
`;

const TemplateGrid = styled.div<{ $visible: boolean }>`
  display: ${props => props.$visible ? 'grid' : 'none'};
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  padding: 12px;
  overflow-y: auto;
  flex: 1;
`;

const TemplateCard = styled.div<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px;
  background: ${props => props.$selected ? 'rgba(76, 175, 80, 0.2)' : '#0a0a15'};
  border: 1px solid ${props => props.$selected ? '#4CAF50' : '#333'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$selected ? 'rgba(76, 175, 80, 0.3)' : '#1a1a2e'};
    border-color: ${props => props.$selected ? '#4CAF50' : '#555'};
  }
`;

const TemplateIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const TemplateName = styled.span`
  font-size: 14px;
  color: #FFFFFF;
  font-weight: 600;
  text-align: center;
  line-height: 1.2;
`;

const CollapsedIcons = styled.div<{ $visible: boolean }>`
  display: ${props => props.$visible ? 'flex' : 'none'};
  flex-direction: column;
  gap: 4px;
  padding: 8px 4px;
  overflow-y: auto;
`;

const CollapsedIconButton = styled.button<{ $active: boolean }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$active ? 'rgba(76, 175, 80, 0.2)' : '#0a0a15'};
  border: 1px solid ${props => props.$active ? '#4CAF50' : '#333'};
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: ${props => props.$active ? 'rgba(76, 175, 80, 0.3)' : '#1a1a2e'};
  }

  svg {
    width: 24px;
    height: 24px;
    color: #fff;
  }
`;

const Instructions = styled.div<{ $visible: boolean }>`
  display: ${props => props.$visible ? 'block' : 'none'};
  padding: 12px;
  background: rgba(76, 175, 80, 0.1);
  border-top: 1px solid #333;
  font-size: 12px;
  color: #FFFFFF;
  font-weight: 500;
  text-align: center;
`;

interface TemplateLibraryProps {
  onSelectTemplate: (element: Partial<SchemaElement>) => void;
  selectedTemplateId: string | null;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const TemplateLibrary: React.FC<TemplateLibraryProps> = ({
  onSelectTemplate,
  selectedTemplateId,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const [activeCategory, setActiveCategory] = useState<TemplateCategory>('structures');

  const filteredTemplates = TEMPLATE_ITEMS.filter(item => item.category === activeCategory);

  const handleTemplateClick = (template: TemplateItem) => {
    onSelectTemplate({
      ...template.element,
      id: `template-${template.id}-${Date.now()}`,
      label: template.name,
      type: 'icon',  // Use icon type for proper rendering
      iconType: template.icon as import('../../types/mapEditor').IconType,
    });
  };

  const categories: TemplateCategory[] = ['structures', 'barriers', 'cover', 'zones', 'markers'];

  return (
    <LibraryContainer $isCollapsed={isCollapsed}>
      <LibraryHeader>
        <HeaderTitle $visible={!isCollapsed}>Template Library</HeaderTitle>
        <CollapseButton onClick={onToggleCollapse} title={isCollapsed ? 'Expand' : 'Collapse'}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            {isCollapsed ? (
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
            ) : (
              <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
            )}
          </svg>
        </CollapseButton>
      </LibraryHeader>

      {/* Expanded view */}
      <CategoryTabs $visible={!isCollapsed}>
        {categories.map(cat => (
          <CategoryTab
            key={cat}
            $active={activeCategory === cat}
            onClick={() => setActiveCategory(cat)}
          >
            {CATEGORY_NAMES[cat]}
          </CategoryTab>
        ))}
      </CategoryTabs>

      <TemplateGrid $visible={!isCollapsed}>
        {filteredTemplates.map(template => (
          <TemplateCard
            key={template.id}
            $selected={selectedTemplateId === template.id}
            onClick={() => handleTemplateClick(template)}
            title={template.name}
          >
            <TemplateIcon>
              {getTemplateIcon(template.icon)}
            </TemplateIcon>
            <TemplateName>{template.name}</TemplateName>
          </TemplateCard>
        ))}
      </TemplateGrid>

      {/* Collapsed view - just icons for quick access */}
      <CollapsedIcons $visible={isCollapsed}>
        {categories.map(cat => (
          <CollapsedIconButton
            key={cat}
            $active={activeCategory === cat}
            onClick={() => {
              setActiveCategory(cat);
              if (onToggleCollapse) onToggleCollapse();
            }}
            title={CATEGORY_NAMES[cat]}
          >
            {cat === 'structures' && (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 3L4 9v12h5v-7h6v7h5V9z" />
              </svg>
            )}
            {cat === 'barriers' && (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm2 4v-2H3a2 2 0 002 2zM3 9h2V7H3v2zm12 12h2v-2h-2v2zm4-18H9a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2zm0 12H9V5h10v10zm-8 6h2v-2h-2v2zm-4 0h2v-2H7v2z" />
              </svg>
            )}
            {cat === 'cover' && (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 4l-4 4h3v7c0 1.1-.9 2-2 2s-2-.9-2-2V9c0-2.21-1.79-4-4-4S5 6.79 5 9v7H2l4 4 4-4H7V9c0-1.1.9-2 2-2s2 .9 2 2v6c0 2.21 1.79 4 4 4s4-1.79 4-4V8h3l-4-4z" />
              </svg>
            )}
            {cat === 'zones' && (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
              </svg>
            )}
            {cat === 'markers' && (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
            )}
          </CollapsedIconButton>
        ))}
      </CollapsedIcons>

      <Instructions $visible={!isCollapsed && selectedTemplateId !== null}>
        Click on the map to place the selected element
      </Instructions>
    </LibraryContainer>
  );
};

export default TemplateLibrary;
